import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import type { Customer, CustomerFilters, PaginatedResponse } from '@/types';

/**
 * Contact-list filters, including the one param the shared
 * `customerService.getCustomers` does not forward yet.
 *
 * It lives here rather than in the service for the same reason
 * `pages/policies/policyListQuery.ts` exists: `src/services/**` (and
 * `src/types/index.ts`, where `CustomerFilters` is declared) are outside this
 * task's file domain. Fold `ordering` back into `customerService.getCustomers`
 * and delete this module the next time that file is edited.
 */
export interface CustomerListFilters extends CustomerFilters {
  /**
   * U-04: DRF `OrderingFilter`'s param -- a field name, optionally with a
   * leading `-` for descending (`?ordering=-full_name`).
   *
   * Only the names in `CustomerViewSet.ordering_fields`
   * (`backend/apps/customers/views.py:198`) do anything. DRF DROPS an
   * unrecognised term silently and returns the default order rather than
   * erroring, which is exactly how a header that looks like a sort control but
   * does nothing ships unnoticed -- so `CustomersListPage` marks only the
   * columns in that list as `sortable`.
   */
  ordering?: string;
}

/**
 * Build the `GET /customers/` query string.
 *
 * Mirrors `customerService.getCustomers` (src/services/customers.ts) and adds
 * `ordering`. Every param only ever NARROWS the caller's already tenant-scoped
 * contact book (the scoping happens in `CustomerViewSet.get_queryset` before
 * any filter runs), so nothing here can widen what a broker/POSP sees.
 */
export function buildCustomerListParams(page: number, filters?: CustomerListFilters): string {
  const params = new URLSearchParams();
  params.append('page', String(page));

  if (filters?.search) params.append('search', filters.search);
  if (filters?.customer_type) params.append('customer_type', filters.customer_type);
  if (filters?.contact_type) params.append('contact_type', filters.contact_type);
  if (filters?.segment) params.append('segment', filters.segment);
  if (filters?.kyc_status) params.append('kyc_status', filters.kyc_status);
  if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
  if (filters?.ordering) params.append('ordering', filters.ordering);
  if (filters?.page_size) params.append('page_size', String(filters.page_size));

  return params.toString();
}

/**
 * The contact list query used by CustomersListPage.
 *
 * The query key keeps the `['customers', ...]` prefix that every customer
 * mutation (and this page's own bulk delete) invalidates, so it stays in step
 * with the rest of the app exactly as `useCustomers` does.
 */
export function useCustomerList(page: number = 1, filters?: CustomerListFilters) {
  return useQuery({
    queryKey: ['customers', page, filters],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<Customer>>(
        `/customers/?${buildCustomerListParams(page, filters)}`
      );
      return response.data;
    },
    placeholderData: (previousData) => previousData,
  });
}
